import { useState, useEffect } from 'react';
import { Fetch } from '../helpers/fetchWrapper';
import { useCancellablePromise } from '../helpers/promiseHandler';

const STATUS = {
  LOADING: 'loading',
  SUCCESS: 'success',
  ERROR: 'error',
};

const dashboardService = {
  _url: `${process.env.REACT_APP_USER_API_URL}/dashboard`,

  GetTopCampaigns(searchQuery, fetch) {
    const [topCampaigns, setTopCampaigns] = useState({
      topCampaigns: [],
      totalItems: 0,
    });
    const { cancellablePromise } = useCancellablePromise();
    const [status, setStatus] = useState(STATUS.LOADING);
    useEffect(() => {
      setStatus(STATUS.LOADING);
      cancellablePromise(this.getTopCampaigns(searchQuery))
        .then(res => {
          setTopCampaigns(() => res);
          setStatus(STATUS.SUCCESS);
        })
        .catch(() => setStatus(STATUS.ERROR));
    }, [JSON.stringify(searchQuery), fetch]);

    return {
      top_campaigns_loading: status === STATUS.LOADING,
      top_campaigns_error: status === STATUS.ERROR,
      top_campaigns_data: topCampaigns,
    };
  },

  GetLatestActivities(fetch) {
    const [latestActivities, setLatestActivities] = useState({
      latestActivities: [],
      totalItems: 0,
    });
    const { cancellablePromise } = useCancellablePromise();
    const [status, setStatus] = useState(STATUS.LOADING);
    useEffect(() => {
      setStatus(STATUS.LOADING);
      cancellablePromise(this.getLatestActivities())
        .then(res => {
          setLatestActivities(() => res);
          setStatus(STATUS.SUCCESS);
        })
        .catch(() => setStatus(STATUS.ERROR));
    }, [fetch]);

    return {
      latest_activities_loading: status === STATUS.LOADING,
      latest_activities_error: status === STATUS.ERROR,
      latest_activities_data: latestActivities,
    };
  },

  GetKPIAnalytics(searchQuery, fetch) {
    const [kpi, setKpi] = useState({
      kpi: [],
      totalItems: 0,
    });
    const { cancellablePromise } = useCancellablePromise();
    const [status, setStatus] = useState(STATUS.LOADING);
    useEffect(() => {
      if (!searchQuery) {
        setStatus(STATUS.SUCCESS);
        return;
      }
      setStatus(STATUS.LOADING);
      // const workspaceId = searchQuery?.workspaceId || '';
      cancellablePromise(this.getKpiAnalytics(searchQuery))
        .then(res => {
          setKpi(() => res);
          setStatus(STATUS.SUCCESS);
        })
        .catch(() => setStatus(STATUS.ERROR));
    }, [JSON.stringify(searchQuery), fetch]);

    return {
      kpi_loading: status === STATUS.LOADING,
      kpi_error: status === STATUS.ERROR,
      kpi_data: kpi,
    };
  },

  GetGraphData(searchQuery, fetch) {
    const [graph, setGraph] = useState({
      graph: [],
      totalItems: 0,
    });
    const { cancellablePromise } = useCancellablePromise();
    const [status, setStatus] = useState(STATUS.LOADING);
    useEffect(() => {
      if (!searchQuery) {
        setStatus(STATUS.SUCCESS);
        return;
      }
      setStatus(STATUS.LOADING);
      cancellablePromise(this.getGraphData(searchQuery))
        .then(res => {
          setGraph(() => res);
          setStatus(STATUS.SUCCESS);
        })
        .catch(() => setStatus(STATUS.ERROR));
    }, [JSON.stringify(searchQuery), fetch]);

    return {
      graph_loading: status === STATUS.LOADING,
      graph_error: status === STATUS.ERROR,
      graph_data: graph,
    };
  },

  GetLinkedinAnalytics(searchQuery, fetch) {
    const [linkedinAnalytics, setLinkedinAnalytics] = useState({});
    const { cancellablePromise } = useCancellablePromise();
    const [status, setStatus] = useState(STATUS.LOADING);
    useEffect(() => {
      if (!searchQuery) {
        setStatus(STATUS.SUCCESS);
        return;
      }
      setStatus(STATUS.LOADING);
      cancellablePromise(this.getLinkedinAnalytics(searchQuery))
        .then(res => {
          setLinkedinAnalytics(() => res);
          setStatus(STATUS.SUCCESS);
        })
        .catch(() => setStatus(STATUS.ERROR));
    }, [JSON.stringify(searchQuery), fetch]);

    return {
      linkedin_loading: status === STATUS.LOADING,
      linkedin_error: status === STATUS.ERROR,
      linkedin_data: linkedinAnalytics,
    };
  },

  GetNotifications(searchQuery, fetch) {
    const [notifications, setNotifications] = useState({
      notifications: [],
      totalItems: 0,
    });
    const { cancellablePromise } = useCancellablePromise();
    const [status, setStatus] = useState(STATUS.LOADING);
    useEffect(() => {
      setStatus(STATUS.LOADING);
      cancellablePromise(this.getNotifications(searchQuery))
        .then(res => {
          setNotifications(() => res);
          setStatus(STATUS.SUCCESS);
        })
        .catch(() => setStatus(STATUS.ERROR));
    }, [JSON.stringify(searchQuery), fetch]);

    return {
      notifications_loading: status === STATUS.LOADING,
      notifications_error: status === STATUS.ERROR,
      notifications_data: notifications,
    };
  },

  GetCampaignsByMember(searchQuery, fetch) {
    const [campaigns, setCampaigns] = useState({ campaigns: [], totalItems: 0 });
    const { cancellablePromise } = useCancellablePromise();
    const [status, setStatus] = useState(STATUS.LOADING);
    useEffect(() => {
      if (searchQuery?.skipFetch === true || !searchQuery?.userId) {
        setStatus(STATUS.SUCCESS);
        setCampaigns({ campaigns: [], totalItems: 0 });
        return;
      }

      setStatus(STATUS.LOADING);
      cancellablePromise(this.getCampaignsByMember(searchQuery))
        .then(res => {
          setCampaigns(() => res);
          setStatus(STATUS.SUCCESS);
        })
        .catch(() => setStatus(STATUS.ERROR));
    }, [JSON.stringify(searchQuery), fetch]);
    return {
      campaigns_loading: status === STATUS.LOADING,
      campaigns_error: status === STATUS.ERROR,
      campaigns_data: campaigns?.data,
    };
  },

  async getKpiAnalytics({ campaignId = '', memberUserId = '', status = '', dateRange = '' }) {
    let url = `${this._url}/get-kpi-analytics?status=${status}&dateRange=${dateRange}`;
    if (campaignId) url += `&campaignId=${campaignId}`;
    if (memberUserId) url += `&memberUserId=${memberUserId}`;
    let res = await Fetch.get(url);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async getGraphData({ status = '', dateRange = '', campaignId = '', memberUserId = '' }) {
    let url = `${this._url}/get-graph-analytics?status=${status}&dateRange=${dateRange}`;
    if (campaignId) url += `&campaignId=${campaignId}`;
    if (memberUserId) url += `&memberUserId=${memberUserId}`;
    let res = await Fetch.get(url);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async getLinkedinAnalytics({ status = '', dateRange = '', campaignId = '', memberUserId = '' }) {
    let url = `${this._url}/get-linkedin-analytics?status=${status}&dateRange=${dateRange}`;
    if (campaignId) url += `&campaignId=${campaignId}`;
    if (memberUserId) url += `&memberUserId=${memberUserId}`;
    let res = await Fetch.get(url);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async getNotifications({ page = 1, itemsPerPage = 10, getAll = false }) {
    let res = await Fetch.get(
      `${this._url}/get-notifications?page=${page}&itemsPerPage=${itemsPerPage}&getAll=${getAll}`,
    );
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async getTopCampaigns({ dateRange }) {
    let res = await Fetch.get(
      `${this._url}/get-top-campaigns?dateRange=${dateRange}`,
    );
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async getLatestActivities() {
    let res = await Fetch.get(
      `${this._url}/get-last-activities??dateRange=`,
    );
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async markAsread() {
    let res = await Fetch.patch(`${this._url}/mark-notifications-read`);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },


  async getCampaignsByMember({ userId = '', page = 1, itemsPerPage = 10, searchText = '', getAll = false, campaignType = '' }) {
    let url = `${this._url}/get-campaigns-by-member?userId=${userId}&page=${page}&itemsPerPage=${itemsPerPage}&searchText=${encodeURIComponent(searchText)}&getAll=${getAll}`;
    if (campaignType) url += `&campaignType=${campaignType}`;
    let res = await Fetch.get(url);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },
};
export default dashboardService;
